import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses';
import type { ListQuery } from './list-query';

/**
 * A row of the list. `description` is not in the contract's `ExpenseRow` yet (asked on #517): it
 * names a row without a supplier, and it is read only when the server sends it.
 */
export type ExpenseRow = components['schemas']['ExpenseRow'] & { description?: string | null };
export type Owner = ExpenseRow['owner'];
export type ListCan = { create: boolean; createForOthers: boolean; filterByOwner: boolean };

export type ExpensesListState =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      items: readonly ExpenseRow[];
      totalAmount: string;
      total: number;
      can: ListCan;
      /** The people the «Utente» filter offers: the server's list when it sends one. */
      owners: readonly Owner[];
    };

/** Fetches one offset page. A new query replaces the rows; stale responses are ignored. */
export function useExpensesList(query: ListQuery) {
  const [state, setState] = useState<ExpensesListState>({ status: 'loading' });
  const key = JSON.stringify(query);
  const generation = useRef(0);
  const [reloads, setReloads] = useState(0);
  // Every owner read since the page opened: narrowing to one person must not empty the choices.
  const seenOwners = useRef<readonly Owner[]>([]);
  const remember = useCallback((page: Page) => {
    seenOwners.current = mergeOwners(
      seenOwners.current,
      page.owners ?? page.items.map((row) => row.owner),
    );
    return seenOwners.current;
  }, []);

  useEffect(() => {
    const mine = ++generation.current;
    setState({ status: 'loading' });
    void read(JSON.parse(key) as ListQuery).then((page) => {
      if (mine !== generation.current) return;
      if (page === null) {
        setState({ status: 'error' });
        return;
      }
      setState({
        status: 'ready',
        items: page.items,
        totalAmount: page.totalAmount,
        total: page.total,
        can: page.can,
        owners: remember(page),
      });
    });
  }, [key, reloads, remember]);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  return { state, reload } as const;
}

type Page = {
  items: ExpenseRow[];
  totalAmount: string;
  total: number;
  nextCursor: string | null;
  can: ListCan;
  owners?: Owner[];
};

async function read(query: ListQuery): Promise<Page | null> {
  try {
    const { data, error, response } = await apiClient.GET('/v1/expenses', { params: { query } });
    if (error !== undefined || !response.ok || data === undefined) return null;
    // `owners` is not in the contract yet: read it only when it is there and well formed.
    const owners = (data as { owners?: unknown }).owners;
    return {
      ...data,
      ...(Array.isArray(owners) ? { owners: owners as Owner[] } : {}),
    };
  } catch {
    return null;
  }
}

function mergeOwners(known: readonly Owner[], seen: readonly Owner[]): Owner[] {
  const byId = new Map(known.map((owner) => [owner.userId, owner]));
  for (const owner of seen) {
    if (!byId.has(owner.userId)) byId.set(owner.userId, owner);
  }
  return [...byId.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}
